import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";

// Actions
import {
  archiveStretch,
  getRoadStretchesOfOrg,
  openRoadStretchMediasDetailsForm,
  openRoadStretchMetaDataUpdateForm,
  restoreStretch,
  stretchPriceRefresh,
} from "../../../actions/org/OrgRoadStretchActions";
import { getRegionNames } from "../../../actions/regions/RegionActions";
import { openSingleCitySelectionForm } from "../../../actions/SingleCitySelectionFormActions";

// Urls
import { RedirectTo } from "../../../urls/PageLinksURL";

// Constants and Utils
import { formatTimestamp } from "../../../common-utils/date-utils/FormatDateUtils";
import { LoadingData } from "../OrgUtils";
import {
  formatText,
  toLocaleString,
} from "../../../common-utils/string-utils/StringUtils";
import { constructRedirectPath } from "../../../utils/redirect-utils/RedirectUtils";
import {
  FormDataTargets,
  OrgSettingsIndexes,
  UrlKeys,
  DefaultPagination,
  PageSize,
} from "../../../constants/GeneralConstants";
import { useUrlKeyCheck } from "../../../mavin/utils/hooks/HooksUtil";

// Components
import TableHeaders from "../../../components/table/TableHeaders";
import Pagination from "../../../components/pagination/Pagination";
import SearchInput from "../../../components/search-input/SearchInput";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import { CustomButton } from "../../../mavin/components/button/Button";
import NoTableDataMessage from "../../../components/table/NoTableDataMessage";
import SingleCitySelectionForm from "../../../mavin/components/single-city-selection-form/SingleCitySelectionForm";

// Pages and Sections
import OrgSettingsSidebar from "../OrgSettingsSidebar";
import RoadStretchMetaDataUpdateForm from "./RoadStretchMetaDataUpdateForm";
import RoadStretchMediasDetailsForm from "./RoadStretchMediasDetailsForm";

// CSS
import "../OrgSettings.scss";

function getTableHeaders(isToohlSpecific) {
  const headers = [
    {
      title: {
        displayName: "City",
        className: "col-1",
      },
    },
    {
      title: {
        displayName: "Name",
        className: "col-4",
      },
    },
    {
      title: {
        displayName: "Created On",
        className: "col-1",
      },
    },
    {
      title: {
        displayName: "Last Updated",
        className: "col-1",
      },
    },
    {
      title: {
        displayName: "Status",
        className: "col-1",
      },
    },
    {
      title: {
        displayName: "Price",
        className: "col-1",
      },
    },
    {
      title: {
        displayName: "Action",
        className: "col-3 text-center",
      },
    },
  ];

  // remove index 5 (Price) for toohl
  if (isToohlSpecific) {
    headers.splice(5, 1);
  }

  return headers;
}

// Page Components
// ----------------------------------------------------------------------------
function CitySelect({ selectedCityId }) {
  const dispatch = useDispatch();

  // regions name list
  const regionsName = useSelector((state) => state.region.regionsName) || [];
  const regionNamesLoading = useSelector((state) => state.region.namesLoading);

  // selected region data
  const selectedRegion = regionsName.find((item) => item.id === selectedCityId);
  const { label: regionName } = selectedRegion || {};

  // Loading till RoadStretches are fetching
  const stretchesLoading = useSelector(
    (state) => state.orgRoadStretch.orgRoadStretchesLoading
  );

  // loading
  if (regionNamesLoading) {
    return (
      <LoadingData
        dataType="Cities"
        className="align-items-center"
        spinnerClassName="spinner-border-sm mx-1"
        dataTypeClassName="px-1"
      />
    );
  }

  return (
    <button
      type="button"
      className="btn btn-primary shadow-none px-3 dropdown-toggle"
      data-toggle="modal"
      data-target={`#${FormDataTargets.singleCitySelectionForm}`}
      onClick={() => dispatch(openSingleCitySelectionForm())}
      disabled={stretchesLoading}
    >
      {regionName ? regionName : "Select City"}
    </button>
  );
}

function TableActionsSection({ setSearchText, onClickSearch, selectedCityId }) {
  // labels
  const TXT_PLACEHOLDER_SEARCH = "Search Road Stretches";

  function onSearchTextChange(e) {
    setSearchText(e.target.value);
  }

  return (
    <div className="table-header">
      <SearchInput
        placeholder={TXT_PLACEHOLDER_SEARCH}
        onClickSearch={onClickSearch}
        onTextChange={onSearchTextChange}
      />

      {/* City Select */}
      <CitySelect selectedCityId={selectedCityId} />
    </div>
  );
}

function CityName({ stretchInfo }) {
  const { cityName, cityId } = stretchInfo;

  return (
    <td>
      <p className="mb-0">{cityName ?? cityId}</p>
    </td>
  );
}

function StretchName({ stretchInfo }) {
  const { name, id } = stretchInfo;
  const link = RedirectTo.orgRoadStretchViewPageUrl.replace(":stretchId", id);

  return (
    <td>
      <Link
        key={id}
        to={constructRedirectPath(link)}
        className="text-truncate-2 d-inline"
        title={name}
      >
        {name}
      </Link>
    </td>
  );
}

function StretchCreationDate({ stretchInfo }) {
  const { createdOn } = stretchInfo;

  return (
    <td>
      <div>{new Date(createdOn).toDateString()}</div>
    </td>
  );
}

function StretchLastUpdatedOn({ stretchInfo }) {
  const { updatedOn } = stretchInfo;
  return (
    <td>
      <div>{new Date(updatedOn).toDateString()}</div>
    </td>
  );
}

function StretchArchiveStatus({ stretchInfo }) {
  const { updatedOn, isArchived } = stretchInfo || {};

  // archive status
  const archiveStatus = isArchived
    ? `Archived on ${formatTimestamp(updatedOn)}`
    : "Active";

  // archive status
  return (
    <td>
      <p>{archiveStatus}</p>
    </td>
  );
}

function StretchMediaPriceAvg({ stretchInfo }) {
  return <td>{formatText(toLocaleString(stretchInfo.priceAvg))}</td>;
}

function StretchAction({ stretchInfo, isToohlSpecific }) {
  const dispatch = useDispatch();
  const { id, isArchived } = stretchInfo || {};

  // Selector
  const archiveLoading = useSelector(
    (state) => state.orgRoadStretch.isStretchArchiveRestore[id]
  );
  const priceRefreshLoading = useSelector(
    (state) => state.orgRoadStretch.stretchPriceRefreshLoading[id]
  );
  const updateMetaDataLoading = useSelector(
    (state) => state.orgRoadStretch.updateRoadStretchMetaDataLoadingMap[id]
  );

  function handleArchive() {
    const action = isArchived ? restoreStretch : archiveStretch;
    dispatch(action(id));
  }

  function handlePriceRefresh() {
    dispatch(stretchPriceRefresh(id));
  }

  function handleUpdateMetaData() {
    dispatch(openRoadStretchMetaDataUpdateForm(stretchInfo));
  }

  function handleViewMediasDetails() {
    dispatch(openRoadStretchMediasDetailsForm(stretchInfo));
  }

  // actions buttons data
  const actionsData = [
    {
      label: "Refresh Price",
      onClick: handlePriceRefresh,
      loading: priceRefreshLoading,
      className: "mr-2",
      isHide: isToohlSpecific,
    },
    {
      label: isArchived ? "Unarchive" : "Archive",
      onClick: handleArchive,
      loading: archiveLoading,
      className: "mr-2",
    },
  ];

  // adding options for toohl
  if (isToohlSpecific) {
    const options = [
      {
        label: "Meta Data",
        onClick: handleUpdateMetaData,
        loading: updateMetaDataLoading,
        className: "mr-2",
        dataToggle: "modal",
        dataTarget: `#${FormDataTargets.roadStretchMetaDataUpdateForm}`,
      },
      {
        label: "Medias Info",
        onClick: handleViewMediasDetails,
        className: "mr-2",
        dataToggle: "modal",
        dataTarget: `#${FormDataTargets.roadStretchMediasDetailsForm}`,
      },
    ];

    actionsData.push(...options);
  }

  return (
    <td>
      <div className="row">
        {actionsData.map((data, i) => {
          const {
            label,
            onClick,
            loading,
            className,
            dataToggle,
            dataTarget,
            isHide,
          } = data;

          if (isHide) {
            return null;
          }

          return (
            <CustomButton
              key={i}
              displayContent={label}
              onClickFunction={onClick}
              loader={loading}
              isDisabled={loading}
              buttonClassName={`btn btn-link shadow-none p-0 col ${className}`}
              data-target={dataTarget}
              data-toggle={dataToggle}
            />
          );
        })}
      </div>
    </td>
  );
}

function RoadStretchRow({ stretchInfo, isToohlSpecific = false }) {
  return (
    <tr>
      <CityName stretchInfo={stretchInfo} />
      <StretchName stretchInfo={stretchInfo} />
      <StretchCreationDate stretchInfo={stretchInfo} />
      <StretchLastUpdatedOn stretchInfo={stretchInfo} />
      <StretchArchiveStatus stretchInfo={stretchInfo} />
      {!isToohlSpecific && <StretchMediaPriceAvg stretchInfo={stretchInfo} />}
      <StretchAction
        stretchInfo={stretchInfo}
        isToohlSpecific={isToohlSpecific}
      />
    </tr>
  );
}

function RoadStretchTable({ roadStretchList, pagination, onClickPagination }) {
  const { totalCount, pageNo, pageSize } = pagination;

  // Condition to check whether there is Toohl/prooh specific key in the url
  const isToohlSpecific = useUrlKeyCheck(UrlKeys.toohlSpecific);
  const tableHeaders = getTableHeaders(isToohlSpecific);

  // Loading till RoadStretches are fetching
  const stretchesLoading = useSelector(
    (state) => state.orgRoadStretch.orgRoadStretchesLoading
  );

  if (stretchesLoading) {
    return <LoadingData className="p-1" dataType="Road Stretches" />;
  }

  // check stretches present or not
  const isStretchesPresent = roadStretchList.length > 0;

  return (
    <div className="table-responsive">
      <table className="table">
        {/* Table Header */}
        <TableHeaders tableHeaders={tableHeaders} headerClass={"thead"} />

        {/* Table Row */}
        <tbody>
          {!isStretchesPresent && (
            <NoTableDataMessage
              colSpan={7}
              message="No Road Stretches present"
            />
          )}
          {isStretchesPresent &&
            roadStretchList.map((eachStretch) => (
              <RoadStretchRow
                key={eachStretch.id}
                stretchInfo={eachStretch}
                isToohlSpecific={isToohlSpecific}
              />
            ))}
        </tbody>
      </table>

      {/* Table-pagination */}
      <Pagination
        totalCount={totalCount}
        pageNo={pageNo}
        pageSize={pageSize}
        onClick={onClickPagination}
      />
    </div>
  );
}

/**
 * Page
 */
export default function RoadStretchListPage() {
  const dispatch = useDispatch();

  // State
  const [searchText, setSearchText] = useState("");
  const [selectedCityId, setSelectedCityId] = useState("");

  // State : Data
  const roadStretchList = useSelector(
    (state) => state.orgRoadStretch.orgRoadStretches
  );
  const pagination = useSelector(
    (state) => state.orgRoadStretch.orgRoadStretchesPagination
  );

  const openRoadStretchMetaDataUpdateForm = useSelector(
    (state) => state.orgRoadStretch.openModal
  );
  const openRoadStretchMediasDetailsForm = useSelector(
    (state) => state.orgRoadStretch.openRoadStretchMediasDetailsModal
  );

  // region names
  const regionsName = useSelector((state) => state.region.regionsName) || [];

  // single City-Selection-form
  const openSingleCitySelectionForm = useSelector(
    (state) => state.singleCitySelectionModal.openModal
  );

  const pageTitle = "Org :: Road Stretches";
  const pageActions = (
    <Link
      className="btn btn-primary"
      to={constructRedirectPath(RedirectTo.orgRoadStretchCreateUrl)}
    >
      Create New Road Stretch
    </Link>
  );

  // pagination
  const pageNumber = DefaultPagination.pageNumber;
  const pageSize = PageSize.pageSize10;

  // once for regionNames
  useEffect(() => {
    dispatch(getRegionNames());
  }, [dispatch]);

  // for road stretches
  useEffect(() => {
    dispatch(
      getRoadStretchesOfOrg("", selectedCityId, "", pageNumber, pageSize)
    );
  }, [dispatch, selectedCityId]);

  // functions
  function onClickPagination(pageNumber, pageSize) {
    dispatch(
      getRoadStretchesOfOrg(
        "",
        selectedCityId,
        searchText,
        pageNumber,
        pageSize
      )
    );
  }

  function onClickSearch() {
    dispatch(
      getRoadStretchesOfOrg(
        "",
        selectedCityId,
        searchText,
        pageNumber,
        pageSize
      )
    );
  }

  return (
    <>
      <OrgSettingsSidebar activeIdx={OrgSettingsIndexes.roadStretches} />

      <div className="content-wrapper">
        {/** Page Header */}
        <PageHeader title={pageTitle} actions={pageActions} shadow={true} />

        {/** Page Content */}
        <div className="page-content">
          {/** Table Header actions */}
          <TableActionsSection
            setSearchText={setSearchText}
            onClickSearch={onClickSearch}
            selectedCityId={selectedCityId}
          />

          {/** Road-Stretch Table component */}
          <RoadStretchTable
            roadStretchList={Object.values(roadStretchList)}
            pagination={pagination}
            onClickPagination={onClickPagination}
          />
        </div>

        {/* MODALS */}
        {/* RS Meta Data */}
        {openRoadStretchMetaDataUpdateForm && <RoadStretchMetaDataUpdateForm />}

        {/* RS Medias Details */}
        {openRoadStretchMediasDetailsForm && <RoadStretchMediasDetailsForm />}

        {/* MODALS */}
        {openSingleCitySelectionForm && (
          <SingleCitySelectionForm
            regionsName={regionsName}
            onSubmit={(cityId) => setSelectedCityId(cityId)}
            selectedCityId={selectedCityId}
          />
        )}
      </div>
    </>
  );
}
